import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { observer } from 'mobx-react-lite';
import ProgramsPage from '../../../programs-page';
import { ReportProgramResponseType } from '../../../../../api/types/reports/education/programms/report-program-response.type';
import { getProgramDetailsForImportOperation } from './operations/get-program-details-for-import-operation';
import { ProgramYearsDistribution } from '../../../../../api/types/programs/program.type';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableSelectionMultipleChangeEvent,
} from 'primereact/datatable';
import { CourseMetadataType } from '../../../../../api/types/programs/course/course-metadata.type';
import { toast } from 'react-toastify';
import globalProgramStore from '../../../../../stores/program-store';
import { linkCoursesToCurrentProgramOperation } from './operations/link-courses-to-current-program-operation';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';

interface ImportCoursesFromOtherProgramsDialogProps {
  visible: boolean;
  targetProgramYear: number | null;
  onHide: () => void;
}

interface ImportableCourseRow extends CourseMetadataType {
  sourceProgramYear: number | null;
}

const ImportCoursesFromOtherProgramsDialog: React.FC<ImportCoursesFromOtherProgramsDialogProps> =
  observer(({ visible, targetProgramYear, onHide }) => {
    const [loadingProgramDetails, setLoadingProgramDetails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showApproveWarning, setShowApproveWarning] = useState(false);
    const [selectedProgram, setSelectedProgram] =
      useState<ReportProgramResponseType | null>(null);
    const [selectedProgramDetails, setSelectedProgramDetails] =
      useState<ProgramYearsDistribution | null>(null);
    const [selectedCourses, setSelectedCourses] = useState<
      ImportableCourseRow[]
    >([]);

    const allProgramCourses = useMemo<ImportableCourseRow[]>(() => {
      if (!selectedProgramDetails?.details) {
        return [];
      }

      const fromYears = selectedProgramDetails.details.years.flatMap(
        (yearData) =>
          yearData.courses.map((course) => ({
            ...course,
            sourceProgramYear: yearData.year,
          })),
      );

      const unassigned = selectedProgramDetails.details.unAssignCourses.map(
        (course) => ({
          ...course,
          sourceProgramYear: null,
        }),
      );

      return [...fromYears, ...unassigned].sort((a, b) => {
        if (a.sourceProgramYear === null) return -1;
        if (b.sourceProgramYear === null) return 1;
        return a.sourceProgramYear - b.sourceProgramYear;
      });
    }, [selectedProgramDetails]);

    const resetState = () => {
      setSelectedProgram(null);
      setSelectedProgramDetails(null);
      setSelectedCourses([]);
      setLoadingProgramDetails(false);
      setLoading(false);
      setShowApproveWarning(false);
    };

    const handleHide = () => {
      resetState();
      onHide();
    };

    const onProgramSelect = async (
      programDetails: ReportProgramResponseType,
    ) => {
      setLoadingProgramDetails(true);
      const fullProgramDetails = await getProgramDetailsForImportOperation(
        programDetails.branchId,
        programDetails.programId,
      );

      if (fullProgramDetails) {
        setSelectedProgram(programDetails);
        setSelectedProgramDetails(fullProgramDetails);
      }
      setLoadingProgramDetails(false);
    };

    const onApproveSelection = async () => {
      const targetProgramId = globalProgramStore.getCurrentId();
      if (!targetProgramId) {
        toast.warn('לא ניתן לזהות תוכנית נוכחית לייבוא קורסים');
        return;
      }

      const targetProgram = globalProgramStore.programs.get(targetProgramId);
      const targetBranchId = targetProgram?.metadata?.branchId;
      if (!targetBranchId) {
        toast.warn('לא ניתן לזהות שלוחה לתוכנית הנוכחית');
        return;
      }

      const selectedCourseIds = selectedCourses.map((course) => course.id);
      if (selectedCourseIds.length === 0) {
        toast.warn('יש לבחור לפחות קורס אחד');
        return;
      }

      if (targetProgramYear === null || targetProgramYear === undefined) {
        toast.warn('יש לבחור שנת תוכנית ליעד הייבוא');
        return;
      }

      const sourceProgramId = selectedProgram?.programId;
      if (!sourceProgramId) {
        toast.warn('לא ניתן לזהות תוכנית מקור לייבוא הקורסים');
        return;
      }

      const isSuccess = await linkCoursesToCurrentProgramOperation(
        targetBranchId,
        sourceProgramId,
        targetProgramId,
        targetProgramYear,
        selectedCourseIds,
        setLoading,
      );

      if (isSuccess) {
        toast.success('הקורסים יובאו בהצלחה');
        handleHide();
      }
    };

    return (
      <Dialog
        header="יבוא קורסים משנים מתוכניות אחרות"
        visible={visible}
        onHide={handleHide}
        style={{ width: '95vw', maxWidth: '1600px' }}
        modal
        contentStyle={{ direction: 'rtl' }}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              {selectedProgramDetails && (
                <Button
                  label="חזרה לבחירת תוכנית"
                  severity="secondary"
                  outlined
                  onClick={() => {
                    setSelectedProgram(null);
                    setSelectedProgramDetails(null);
                    setSelectedCourses([]);
                  }}
                />
              )}
              <Button label="סגור" severity="secondary" onClick={handleHide} />
            </div>
            {selectedProgramDetails && (
              <Button
                label="אישור"
                icon="pi pi-check"
                onClick={() => setShowApproveWarning(true)}
                disabled={selectedCourses.length === 0 || loading}
                loading={loading}
              />
            )}
          </div>
        }
      >
        {!selectedProgramDetails ? (
          <ProgramsPage
            isAllowProgramNavigation={false}
            onProgramClick={onProgramSelect}
          />
        ) : (
          <div>
            <div
              style={{
                marginBottom: '12px',
                fontWeight: 'bold',
                color: '#1f2937',
              }}
            >
              תוכנית נבחרת: {selectedProgram?.abstractProgramName} (ID:{' '}
              {selectedProgram?.programId}) | קורסים זמינים:{' '}
              {allProgramCourses.length}
            </div>
            <DataTable
              value={allProgramCourses}
              selectionMode="multiple"
              selection={selectedCourses}
              onSelectionChange={(
                e: DataTableSelectionMultipleChangeEvent<ImportableCourseRow[]>,
              ) => {
                setSelectedCourses(e.value ?? []);
              }}
              dataKey="id"
              loading={loadingProgramDetails}
              scrollable
              scrollHeight="calc(100vh - 360px)"
              rowGroupMode="subheader"
              groupRowsBy="sourceProgramYear"
              sortField="sourceProgramYear"
              sortOrder={1}
              rowGroupHeaderTemplate={(rowData: ImportableCourseRow) => (
                <div style={{ fontWeight: 'bold' }}>
                  {rowData.sourceProgramYear === null
                    ? 'יתרה ליישום'
                    : `שנה ${rowData.sourceProgramYear + 1}`}
                </div>
              )}
              tableStyle={{ direction: 'rtl' }}
              emptyMessage={
                <div style={{ textAlign: 'right' }}>
                  לא נמצאו קורסים בתוכנית
                </div>
              }
            >
              <Column
                selectionMode="multiple"
                headerStyle={{ width: '3rem' }}
              />
              <Column
                header="שם הקורס"
                body={(rowData: ImportableCourseRow) => (
                  <div>
                    {[
                      rowData.abstractData?.courseName,
                      rowData.additionalCourseName,
                    ]
                      .filter(Boolean)
                      .join(' - ')}
                  </div>
                )}
                style={{ textAlign: 'right' }}
              />
              <Column
                header="שנה"
                body={(rowData: ImportableCourseRow) =>
                  rowData.sourceProgramYear === null
                    ? 'טרם הוגדר'
                    : `שנה ${rowData.sourceProgramYear + 1}`
                }
                style={{ textAlign: 'right', width: '180px' }}
              />
              <Column
                header="מכסת שעות"
                body={(rowData: ImportableCourseRow) =>
                  rowData.abstractData?.courseHours ?? ''
                }
                style={{ textAlign: 'right', width: '140px' }}
              />
              <Column
                header="סטטוס"
                body={(rowData: ImportableCourseRow) => rowData.status}
                style={{ textAlign: 'right', width: '140px' }}
              />
            </DataTable>
          </div>
        )}
        <WarningDialog
          header="אישור פעולה מסוכנת"
          isVisible={showApproveWarning}
          onHide={() => setShowApproveWarning(false)}
          content="האם לאשר העברת קורסים מתוכנית אחרת לתוכנית הנוכחית?"
          dangerousContent="פעולה זו עשויה לשנות שיוך קורסים בין תוכניות ולהשפיע על נתוני התוכניות."
          smallLetters="מומלץ לוודא שהקורסים שנבחרו אכן מיועדים לשנת היעד שנבחרה."
          notInformation="המערכת תבצע קישור קורסים לתוכנית הנוכחית, כולל שנת התוכנית שנבחרה בעת פתיחת חלון הייבוא."
          notInformationVariant="blue"
          confirmLabel="אישור והמשך"
          cancelLabel="ביטול"
          confirmLoading={loading}
          onConfirm={async () => {
            await onApproveSelection();
            setShowApproveWarning(false);
          }}
        />
      </Dialog>
    );
  });

export default ImportCoursesFromOtherProgramsDialog;
